import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraInput } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useRef, useState } from 'react';
import { useCompanyProfiles } from '../../pages/Delivery/Programs/referenceDataHooks';
import { RegistryEntityAddDialog, useRegistryCreatePermission } from './RegistryEntityPicker';
const T = Testids.sharedRecord;
const SEARCH_DEBOUNCE_MS = 200;
/**
 * Inline shared-Company picker (C7A-12): searches existing records first,
 * shows enough identity to avoid duplicates, and — only for holders of
 * area:registries — can add the missing Company through RegistryEntityPicker's
 * add dialog (GRW-37: UEI/CAGE first, then REG-54 ResolveEntities; a name
 * alone is checked against existing companies before anything is created)
 * and return with its canonical id selected. The typed text is never cleared by search, preview,
 * a failed create or the absence of write authority. The caller's own draft
 * state is never touched here.
 */ export function SharedCompanyPicker({ value, onSelect, onPreview, onLocalTextChange, placeholder = 'Search registry companies…' }) {
    const canCreate = useRegistryCreatePermission('COMPANY');
    const [adding, setAdding] = useState(false);
    const [text, setText] = useState('');
    const [term, setTerm] = useState('');
    const [selectedName, setSelectedName] = useState('');
    // The caller consumed the selection (e.g. it saved the link and cleared it):
    // start the next search clean. Clearing only on that edge keeps typed text
    // intact through every other re-render.
    const hadValue = useRef(false);
    useEffect(()=>{
        if (value) hadValue.current = true;
        else if (hadValue.current) {
            hadValue.current = false;
            setText('');
            setTerm('');
            setSelectedName('');
        }
    }, [
        value
    ]);
    useEffect(()=>{
        const t = setTimeout(()=>setTerm(text.trim()), SEARCH_DEBOUNCE_MS);
        return ()=>clearTimeout(t);
    }, [
        text
    ]);
    const search = useCompanyProfiles(term || undefined);
    const typed = text.trim();
    const results = term ? search.data ?? [] : [];
    const settled = typed === term;
    const exact = results.find((c)=>c.name.trim().toLowerCase() === typed.toLowerCase());
    const choose = (id, name)=>{
        setSelectedName(name);
        onSelect(id, name);
    };
    const create = ()=>{
        setAdding(true);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "min-w-0 space-y-2",
        "data-testid": T.pickerRoot,
        children: [
            value && /*#__PURE__*/ _jsxs("p", {
                className: "break-words text-xs",
                "data-testid": T.pickerSelected,
                "data-record-id": value,
                children: [
                    "Selected: ",
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-medium",
                        children: selectedName || value
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraInput, {
                value: text,
                placeholder: placeholder,
                "data-testid": T.pickerInput,
                onChange: (e)=>{
                    setText(e.target.value);
                    onLocalTextChange?.(e.target.value);
                }
            }),
            term && search.isError && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-1 text-xs",
                role: "alert",
                "data-testid": T.pickerError,
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        children: "Company search is unavailable right now. Your text is kept — retry, or add the company in Registries."
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "secondary",
                        onClick: ()=>search.refetch(),
                        children: "Retry search"
                    })
                ]
            }),
            results.length > 0 && /*#__PURE__*/ _jsx("ul", {
                className: "space-y-1",
                children: results.map((c)=>/*#__PURE__*/ _jsxs("li", {
                        className: "flex min-w-0 items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsxs(SpecraButton, {
                                variant: "ghost",
                                size: "sm",
                                className: "h-auto min-w-0 flex-1 flex-col items-start whitespace-normal break-words rounded-md border px-3 py-2 text-left",
                                "data-testid": T.pickerResult,
                                "data-record-id": c.id,
                                onClick: ()=>choose(c.id, c.name),
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "font-medium",
                                        children: c.name
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-xs text-muted-foreground",
                                        children: [
                                            'Company',
                                            c.isOwnCompany ? 'Your organization' : '',
                                            c.cageCode ? `CAGE ${c.cageCode}` : '',
                                            c.uei ? `UEI ${c.uei}` : ''
                                        ].filter(Boolean).join(' · ')
                                    })
                                ]
                            }),
                            onPreview && /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "ghost",
                                size: "sm",
                                className: "shrink-0",
                                "data-testid": T.pickerResultPreview,
                                onClick: ()=>onPreview(c.id),
                                children: "Preview"
                            })
                        ]
                    }, c.id))
            }),
            typed && settled && !search.isError && !search.isLoading && results.length === 0 && /*#__PURE__*/ _jsxs("p", {
                className: "text-xs text-muted-foreground",
                "data-testid": T.pickerEmpty,
                children: [
                    'No existing company matches "',
                    typed,
                    '".'
                ]
            }),
            typed && settled && !search.isError && exact && /*#__PURE__*/ _jsxs("p", {
                className: "text-xs text-warning",
                "data-testid": T.pickerDuplicate,
                children: [
                    'A company named "',
                    exact.name,
                    '" already exists — select it instead of creating a duplicate.'
                ]
            }),
            typed && settled && !search.isError && !search.isLoading && !exact && canCreate && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-1",
                children: [
                    /*#__PURE__*/ _jsxs(SpecraButton, {
                        size: "sm",
                        variant: "secondary",
                        className: "h-auto max-w-full whitespace-normal break-words py-1.5 text-left",
                        "data-testid": T.pickerCreate,
                        onClick: create,
                        children: [
                            'Add company "',
                            typed,
                            '"'
                        ]
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-xs text-muted-foreground",
                        children: "Creates a shared Company in Registries, visible beyond this record. Your other entries here are kept."
                    })
                ]
            }),
            typed && settled && !search.isError && !search.isLoading && !exact && !canCreate && /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                "data-testid": T.pickerNoCreate,
                children: "You can't create shared companies. Your text stays here as a working note; ask a Registries administrator to add it."
            }),
            adding && /*#__PURE__*/ _jsx(RegistryEntityAddDialog, {
                kind: "COMPANY",
                initialText: typed,
                onPick: (o)=>choose(o.registryId, o.label),
                onClose: ()=>setAdding(false)
            })
        ]
    });
}
